import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listEmails,
  getEmail,
  createEmail,
  updateEmail,
  deleteEmail,
  sendEmail,
  scheduleEmail,
  listEmailTemplates,
  listMergeTags,
  getEmailApiErrorMessage,
} from '../api/emails';

export const emailKeys = {
  all: ['emails'],
  list: (filters) => [...emailKeys.all, 'list', filters],
  detail: (id) => [...emailKeys.all, 'detail', id],
  templates: () => [...emailKeys.all, 'templates'],
  mergeTags: () => [...emailKeys.all, 'mergeTags'],
};

export function useEmailsList({ clientId, status, page = 1, enabled = true }) {
  return useQuery({
    queryKey: emailKeys.list({ clientId, status, page }),
    queryFn: async () => {
      const params = { page };
      if (clientId != null) params.client_id = clientId;
      if (status) params.status = status;
      return listEmails(params);
    },
    enabled,
  });
}

export function useEmail(id, enabled = true) {
  return useQuery({
    queryKey: emailKeys.detail(id),
    queryFn: () => getEmail(id),
    enabled: enabled && Boolean(id),
  });
}

export function useCreateEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createEmail,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailKeys.all });
    },
  });
}

export function useUpdateEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateEmail(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailKeys.all });
    },
  });
}

export function useDeleteEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteEmail,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailKeys.all });
    },
  });
}

export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sendEmail,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailKeys.all });
    },
  });
}

export function useScheduleEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduledAtIso }) => scheduleEmail(id, scheduledAtIso),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: emailKeys.all });
    },
  });
}

export function useEmailTemplates(options = {}) {
  return useQuery({
    queryKey: emailKeys.templates(),
    queryFn: async () => {
      const raw = await listEmailTemplates();
      if (Array.isArray(raw)) return raw;
      return raw?.results ?? raw?.data ?? [];
    },
    ...options,
  });
}

export function useMergeTags(options = {}) {
  return useQuery({
    queryKey: emailKeys.mergeTags(),
    queryFn: async () => {
      const raw = await listMergeTags();
      if (Array.isArray(raw)) return raw;
      return raw?.tags ?? raw?.results ?? [];
    },
    ...options,
  });
}

export { getEmailApiErrorMessage };
